import { gql } from '@apollo/client';

// System Settings
export const GET_ALLOW_TELECONSULTATION = gql`
  query AllowTeleconsultation {
    allowTeleconsultation
  }
`;

export const GET_TELECONSULTATION_FEE = gql`
  query TeleconsultationFee {
    teleconsultationFee(tenant: "default")
  }
`;

export const GET_TELECONSULTATION_FEE_CHINA_LIFE = gql`
  query TeleconsultationFeeChinaLife {
    teleconsultationFee(tenant: "china-life")
  }
`;

export const GET_TELECONSULTATION_LEAD_TIME = gql`
  query TeleconsultationLeadTime {
    teleconsultationLeadTime
  }
`;

export const GET_REMOTE_AREA_DELIVERY_EXTRA_FEE = gql`
  query RemoteAreaDeliveryExtraFee {
    remoteAreaDeliveryExtraFee
  }
`;

export const GET_BOOKING_DAYS_LIMIT = gql`
  query BookingDaysLimit {
    bookingDaysLimit
  }
`;

export const GET_BOOKING_OPEN_HOURS = gql`
  query BookingOpenHours {
    bookingOpenHours
  }
`;

export const GET_DOCTOR_SPECIALIZATIONS = gql`
  query DoctorSpecializations($locale: String) {
    doctorSpecializations(locale: $locale)
  }
`;

// New schedule-based queries
export const GET_BOOKING_SCHEDULES = gql`
  query BookingSchedules {
    bookingSchedules {
      id
      name
      isDefault
      fromDate
      toDate
      priority
      isActive
      createdAt
      updatedAt
      schedule {
        monday {
          from
          to
        }
        tuesday {
          from
          to
        }
        wednesday {
          from
          to
        }
        thursday {
          from
          to
        }
        friday {
          from
          to
        }
        saturday {
          from
          to
        }
        sunday {
          from
          to
        }
      }
    }
  }
`;

export const GET_ACTIVE_BOOKING_SCHEDULES = gql`
  query ActiveBookingSchedules {
    activeBookingSchedules {
      id
      name
      isDefault
      fromDate
      toDate
      priority
      isActive
      createdAt
      updatedAt
      schedule {
        monday {
          from
          to
        }
        tuesday {
          from
          to
        }
        wednesday {
          from
          to
        }
        thursday {
          from
          to
        }
        friday {
          from
          to
        }
        saturday {
          from
          to
        }
        sunday {
          from
          to
        }
      }
    }
  }
`;

export const GET_SCHEDULE_OF_DATE = gql`
  query GetScheduleOfDate($date: String!, $tenant: String) {
    scheduleOfDate(date: $date, tenant: $tenant) {
      id
      name
      isDefault
      fromDate
      toDate
      priority
      isActive
      createdAt
      updatedAt
      schedule {
        monday {
          from
          to
        }
        tuesday {
          from
          to
        }
        wednesday {
          from
          to
        }
        thursday {
          from
          to
        }
        friday {
          from
          to
        }
        saturday {
          from
          to
        }
        sunday {
          from
          to
        }
      }
    }
  }
`;

export const SET_ALLOW_TELECONSULTATION = gql`
  mutation SetAllowTeleconsultation($enabled: Boolean!) {
    setAllowTeleconsultation(enabled: $enabled)
  }
`;

export const SET_TELECONSULTATION_FEE = gql`
  mutation SetTeleconsultationFee($fee: Float!, $tenant: String) {
    setTeleconsultationFee(fee: $fee, tenant: $tenant)
  }
`;

export const SET_TELECONSULTATION_LEAD_TIME = gql`
  mutation SetTeleconsultationLeadTime($hours: Float!) {
    setTeleconsultationLeadTime(hours: $hours)
  }
`;

export const SET_REMOTE_AREA_DELIVERY_EXTRA_FEE = gql`
  mutation SetRemoteAreaDeliveryExtraFee($fee: Float!) {
    setRemoteAreaDeliveryExtraFee(fee: $fee)
  }
`;

export const SET_BOOKING_DAYS_LIMIT = gql`
  mutation SetBookingDaysLimit($days: Float!) {
    setBookingDaysLimit(days: $days)
  }
`;

export const SET_BOOKING_OPEN_HOURS = gql`
  mutation SetBookingOpenHours($hours: [String!]!) {
    setBookingOpenHours(hours: $hours)
  }
`;

// New schedule-based mutations
export const CREATE_BOOKING_SCHEDULE = gql`
  mutation CreateBookingSchedule($input: CreateBookingScheduleInput!) {
    createBookingSchedule(input: $input) {
      id
      name
      isDefault
      fromDate
      toDate
      priority
      isActive
      createdAt
      updatedAt
      schedule {
        monday {
          from
          to
        }
        tuesday {
          from
          to
        }
        wednesday {
          from
          to
        }
        thursday {
          from
          to
        }
        friday {
          from
          to
        }
        saturday {
          from
          to
        }
        sunday {
          from
          to
        }
      }
    }
  }
`;

export const UPDATE_BOOKING_SCHEDULE = gql`
  mutation UpdateBookingSchedule($input: UpdateBookingScheduleInput!) {
    updateBookingSchedule(input: $input) {
      id
      name
      isDefault
      fromDate
      toDate
      priority
      isActive
      createdAt
      updatedAt
      schedule {
        monday {
          from
          to
        }
        tuesday {
          from
          to
        }
        wednesday {
          from
          to
        }
        thursday {
          from
          to
        }
        friday {
          from
          to
        }
        saturday {
          from
          to
        }
        sunday {
          from
          to
        }
      }
    }
  }
`;

export const DELETE_BOOKING_SCHEDULE = gql`
  mutation DeleteBookingSchedule($scheduleId: String!) {
    deleteBookingSchedule(scheduleId: $scheduleId)
  }
`;

// Clinic Queries
export const GET_CLINICS = gql`
  query GetClinics {
    clinics {
      id
      name_en
      name_zh_hk
      internal_name
      address
      phone_number
      email
      status
      region
      district
      description
      building_name
      floor
      unit
      postal_code
      website_url
      emergency_services
      walk_in_enabled
      online_consultation_enabled
      appointment_booking_enabled
      facilities
      services
      images
      latitude
      longitude
      created_at
      updated_at
    }
  }
`;

export const GET_CLINIC = gql`
  query GetClinic($id: ID!) {
    clinic(id: $id) {
      id
      name_en
      name_zh_hk
      internal_name
      address
      phone_number
      email
      status
      region
      district
      description
      building_name
      floor
      unit
      postal_code
      website_url
      emergency_services
      walk_in_enabled
      online_consultation_enabled
      appointment_booking_enabled
      facilities
      services
      images
      latitude
      longitude
      created_at
      updated_at
      providers {
        id
        doctor {
          id
          first_name_en
          last_name_en
          specialization
        }
      }
    }
  }
`;

// Doctor Queries
export const GET_DOCTORS = gql`
  query GetDoctors {
    doctors {
      id
      first_name_en
      last_name_en
      first_name_zh_hk
      last_name_zh_hk
      email
      phone_number
      license_number
      specialization
      status
      rating
      experience_years
      consultation_fee
      avatar_url
      bio
      qualification
      title
      languages
      is_available_for_emergency
      max_daily_appointments
      total_reviews
      time_zone
      created_at
      updated_at
      providers {
        id
        clinic {
          id
          name_en
          name_zh_hk
          internal_name
        }
      }
    }
  }
`;

export const GET_DOCTOR = gql`
  query GetDoctor($id: ID!) {
    doctor(id: $id) {
      id
      first_name_en
      last_name_en
      first_name_zh_hk
      last_name_zh_hk
      email
      phone_number
      license_number
      specialization
      status
      rating
      experience_years
      consultation_fee
      avatar_url
      bio
      qualification
      title
      languages
      is_available_for_emergency
      max_daily_appointments
      total_reviews
      time_zone
      created_at
      updated_at
      providers {
        id
        clinic {
          id
          name_en
          name_zh_hk
          internal_name
        }
      }
    }
  }
`;

// Patient Queries
export const GET_PATIENTS = gql`
  query GetPatients {
    allPatients {
      id
      first_name_en
      last_name_en
      first_name_zh_hk
      last_name_zh_hk
      email
      phone_number
      birthdate
      gender
      status
      emergency_contact_name
      emergency_contact_phone
      avatar_url
      blood_type
      nationality
      identity_document_type
      identity_document_number
      language_preferences
      allergies
      chronic_conditions
      medications
      notes
      created_at
      updated_at
      app_user {
        id
        firstname
        lastname
        email
        phone_number
      }
    }
  }
`;

export const GET_PATIENTS_PAGINATED = gql`
  query GetPatientsPaginated($paginationDto: PaginationDto!) {
    patientsPaginated(paginationDto: $paginationDto) {
      patients {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        email
        phone_number
        birthdate
        gender
        status
        emergency_contact_name
        emergency_contact_phone
        avatar_url
        blood_type
        nationality
        identity_document_type
        identity_document_number
        language_preferences
        allergies
        chronic_conditions
        medications
        notes
        created_at
        updated_at
        app_user {
          id
          firstname
          lastname
          email
          phone_number
        }
      }
      pagination {
        page
        limit
        total
        totalPages
        hasNext
        hasPrev
      }
    }
  }
`;

export const GET_PATIENT = gql`
  query GetPatient($id: ID!) {
    patient(id: $id) {
      id
      first_name_en
      last_name_en
      first_name_zh_hk
      last_name_zh_hk
      email
      phone_number
      birthdate
      gender
      status
      emergency_contact_name
      emergency_contact_phone
      avatar_url
      blood_type
      nationality
      identity_document_type
      identity_document_number
      language_preferences
      allergies
      chronic_conditions
      medications
      notes
      created_at
      updated_at
      app_user {
        id
        firstname
        lastname
        email
        phone_number
      }
    }
  }
`;

// Provider Queries
export const GET_PROVIDERS = gql`
  query GetProviders {
    providers {
      id
      doctor_id
      clinic_id
      consultation_fee
      department
      employment_type
      start_date
      end_date
      status
      role
      specialization_at_clinic
      last_worked_at
      verified_at
      created_at
      updated_at
      doctor {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        license_number
        specialization
        status
        rating
        experience_years
        email
        phone_number
      }
      clinic {
        id
        name_en
        name_zh_hk
        internal_name
        address
        region
        district
        phone_number
        email
        status
      }
      tags {
        id
        name
        slug
        category
        status
        display_name
        description
        color
        icon
        sort_order
        usage_count
        last_used_at
        is_system_tag
      }
      schedules {
        id
        status
        max_appointments_per_day
        default_appointment_duration
        is_available_for_telemedicine
        is_available_for_walk_in
        schedule {
          monday {
            from
            to
          }
          tuesday {
            from
            to
          }
          wednesday {
            from
            to
          }
          thursday {
            from
            to
          }
          friday {
            from
            to
          }
          saturday {
            from
            to
          }
          sunday {
            from
            to
          }
        }
      }
    }
  }
`;

// Tag Queries
export const GET_TAGS = gql`
  query GetTags {
    allTags {
      id
      name
      slug
      category
      status
      display_name
      description
      color
      icon
      sort_order
      usage_count
      last_used_at
      is_system_tag
    }
  }
`;

export const GET_PROVIDER = gql`
  query GetProvider($id: ID!) {
    provider(id: $id) {
      id
      doctor_id
      clinic_id
      consultation_fee
      department
      employment_type
      start_date
      end_date
      status
      role
      specialization_at_clinic
      last_worked_at
      verified_at
      created_at
      updated_at
      doctor {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        license_number
        specialization
        status
        rating
        experience_years
        email
        phone_number
      }
      clinic {
        id
        name_en
        name_zh_hk
        internal_name
        address
        region
        district
        phone_number
        email
        status
      }
      tags {
        id
        name
        slug
        category
        status
        display_name
        description
        color
        icon
        sort_order
        usage_count
        last_used_at
        is_system_tag
      }
      schedules {
        id
        status
        max_appointments_per_day
        default_appointment_duration
        is_available_for_telemedicine
        is_available_for_walk_in
        schedule {
          monday {
            from
            to
          }
          tuesday {
            from
            to
          }
          wednesday {
            from
            to
          }
          thursday {
            from
            to
          }
          friday {
            from
            to
          }
          saturday {
            from
            to
          }
          sunday {
            from
            to
          }
        }
      }
    }
  }
`;

// Utility Queries
export const GET_HK_REGIONS_AND_DISTRICTS = gql`
  query GetHKRegionsAndDistricts {
    hkRegionsAndDistricts {
      regions {
        code
        name_en
        name_zh_hk
        districts {
          code
          name_en
          name_zh_hk
        }
      }
    }
  }
`;

// Filtered Queries
export const GET_CLINICS_BY_REGION = gql`
  query GetClinicsByRegion($region: String!) {
    clinicsByRegion(region: $region) {
      id
      name_en
      name_zh_hk
      internal_name
      address
      phone_number
      email
      status
      region
      district
    }
  }
`;

export const GET_DOCTORS_BY_SPECIALIZATION = gql`
  query GetDoctorsBySpecialization($specialization: String!) {
    doctorsBySpecialization(specialization: $specialization) {
      id
      first_name_en
      last_name_en
      first_name_zh_hk
      last_name_zh_hk
      email
      phone_number
      license_number
      specialization
      status
      rating
      experience_years
      consultation_fee
    }
  }
`;

export const GET_PROVIDERS_BY_CLINIC = gql`
  query GetProvidersByClinic($clinicId: ID!) {
    providersByClinic(clinicId: $clinicId) {
      id
      doctor_id
      clinic_id
      consultation_fee
      status
      doctor {
        id
        first_name_en
        last_name_en
        specialization
      }
      clinic {
        id
        name_en
        name_zh_hk
        internal_name
      }
    }
  }
`;

// Appointment Queries
export const GET_APPOINTMENTS = gql`
  query GetAppointments($filter: AppointmentFilterDto) {
    appointments(filter: $filter) {
      id
      app_user_id
      patient_id
      provider_id
      date
      time
      expected_time_range
      duration
      status
      queue_number
      type
      reservation_type
      questionnaire
      appointment_notes
      internal_remark
      priority
      consultation_type
      language_preference
      booking_source
      payment_status
      payment_method
      total_amount
      insurance_claim_id
      timezone
      # reminder_sent
      confirmation_sent
      # rescheduled_from
      # rescheduled_to
      cancelled_reason
      cancelled_by
      medical_history_reviewed
      diagnosis
      treatment_plan
      prescription
      follow_up_required
      follow_up_date
      patient_phone
      patient_email
      emergency_contact
      special_instructions
      patient_rating
      patient_feedback
      doctor_notes
      quality_score
      created_at
      updated_at
      scheduled_at
      confirmed_at
      started_at
      completed_at
      cancelled_at
      otherStatuses
      app_user {
        id
        firstname
        lastname
        email
        phone_number
        tenant
      }
      patient {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        gender
        birthdate
        email
        phone_number
        status
        relationship_to_owner
      }
    }
  }
`;

export const GET_APPOINTMENT = gql`
  query GetAppointment($id: String!) {
    appointment(id: $id) {
      id
      app_user_id
      patient_id
      provider_id
      date
      time
      expected_time_range
      duration
      status
      type
      reservation_type
      questionnaire
      appointment_notes
      internal_remark
      priority
      consultation_type
      language_preference
      booking_source
      payment_status
      payment_method
      total_amount
      insurance_claim_id
      timezone
      reminder_sent
      confirmation_sent
      rescheduled_from
      rescheduled_to
      cancelled_reason
      cancelled_by
      medical_history_reviewed
      diagnosis
      treatment_plan
      prescription
      follow_up_required
      follow_up_date
      patient_phone
      patient_email
      emergency_contact
      special_instructions
      delivery_address_id
      delivery_note
      delivery_address {
        id
        type
        display_name
        region
        district
        street_address
        building_name
        floor
        block
        unit
        postal_code
        override_address
        is_default
        phone_number
        contact_name
        created_at
        updated_at
      }
      sfExpressOrders {
        id
        tenant
        request_id
        service_code
        timestamp
        partner_id
        msg_digest
        request_data
        response_data
        status
        order_id
        waybill_no
        tracking_number
        error_code
        error_message
        api_result_code
        api_error_msg
        notes
        created_at
        updated_at
        processed_at
        retry_count
        last_retry_at
        metadata
        appointment_id
      }
      jdExpressOrders {
        id
        tenant
        request_id
        service_code
        timestamp
        partner_id
        msg_digest
        request_data
        response_data
        status
        order_id
        waybill_no
        tracking_number
        error_code
        error_message
        api_result_code
        api_error_msg
        notes
        created_at
        updated_at
        processed_at
        retry_count
        last_retry_at
        metadata
        appointment_id
      }
      transactions {
        id
        app_user_id
        appointment_id
        transaction_reference
        original_amount
        amount
        currency
        type
        status
        payment_method
        description
        category
        subcategory
        notes
        gateway_transaction_id
        gateway_response
        processing_fee
        tax_amount
        discount_amount
        final_amount
        metadata
        refund_reason
        refunded_by
        refunded_at
        original_transaction_id
        created_at
        updated_at
        processed_at
        completed_at
        items {
          id
          name
          quantity
          price
        }
      }
      agoraLink {
        id
        appointmentId
        channelName
        uid
        role
        token
        link
        webLink
        appId
        expiresAt
        isActive
        createdAt
        updatedAt
      }
      insuranceClaims {
        id
        policyId
        appointmentId
        status
        claimResultDetails {
          approved_amount
          rejected_amount
          rejected_reason
        }
        isActive
        createdAt
        updatedAt
        items {
          id
          insuranceClaimId
          name
          category
          amount
          approvedAmount
          transactionId
          createdAt
          updatedAt
        }
      }
      patient_rating
      patient_feedback
      doctor_notes
      quality_score
      created_at
      updated_at
      scheduled_at
      confirmed_at
      started_at
      completed_at
      cancelled_at
      app_user {
        id
        firstname
        lastname
        email
        phone_number
        tenant
        addresses {
          id
          type
          street_address
          unit
          floor
          building_name
          district
          region
          postal_code
          is_default
          created_at
          updated_at
        }
        patients {
          id
          first_name_en
          last_name_en
          first_name_zh_hk
          last_name_zh_hk
          email
          phone_number
          birthdate
          gender
          status
          emergency_contact_name
          emergency_contact_phone
          avatar_url
          blood_type
          nationality
          identity_document_type
          identity_document_number
          language_preferences
          allergies
          chronic_conditions
          medications
          notes
          relationship_to_owner
          created_at
          updated_at
        }
      }
      patient {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        gender
        birthdate
        email
        phone_number
        status
        relationship_to_owner
        emergency_contact_name
        emergency_contact_phone
        blood_type
        nationality
        identity_document_type
        identity_document_number
        language_preferences
        allergies
        chronic_conditions
        medications
        notes
        avatar_url
        created_at
        updated_at
      }
      provider {
        id
        doctor_id
        clinic_id
        consultation_fee
        department
        employment_type
        start_date
        end_date
        status
        role
        specialization_at_clinic
        last_worked_at
        verified_at
        created_at
        updated_at
        doctor {
          id
          first_name_en
          last_name_en
          first_name_zh_hk
          last_name_zh_hk
          license_number
          specialization
          status
          rating
          experience_years
          email
          phone_number
          avatar_url
          bio
          qualification
          title
          languages
        }
        clinic {
          id
          name_en
          name_zh_hk
          internal_name
          address
          region
          district
          phone_number
          email
          status
          building_name
          floor
          unit
        }
      }
    }
  }
`;

export const GET_APPOINTMENTS_BY_DATE_RANGE = gql`
  query GetAppointmentsByDateRange($providerId: String!, $startDate: String!, $endDate: String!) {
    appointmentsByDateRange(providerId: $providerId, startDate: $startDate, endDate: $endDate) {
      id
      app_user_id
      provider_id
      date
      time
      duration
      status
      type
      reservation_type
      questionnaire
      appointment_notes
      priority
      consultation_type
      language_preference
      booking_source
      payment_status
      payment_method
      total_amount
      insurance_claim_id
      timezone
      reminder_sent
      confirmation_sent
      rescheduled_from
      rescheduled_to
      cancelled_reason
      cancelled_by
      medical_history_reviewed
      diagnosis
      treatment_plan
      prescription
      follow_up_required
      follow_up_date
      patient_phone
      patient_email
      emergency_contact
      special_instructions
      patient_rating
      patient_feedback
      doctor_notes
      quality_score
      tenant
      created_at
      updated_at
      scheduled_at
      confirmed_at
      started_at
      completed_at
      cancelled_at
    }
  }
`;

export const GET_APPOINTMENT_STATS = gql`
  query GetAppointmentStats($userId: String, $providerId: String) {
    appointmentStats(userId: $userId, providerId: $providerId) {
      scheduled
      confirmed
      in_progress
      completed
      cancelled
      no_show
    }
  }
`;

export const GET_TODAYS_APPOINTMENT_SUMMARY = gql`
  query GetTodaysAppointmentSummary {
    getTodaysAppointmentSummary {
      appointmentDateTime
      status
      payment
      extraMed
      deliveryWaybill
      timestamp
      lastnameEn
      firstnameEn
      chineseName
      gender
      dob
      hkid
      appUserPhone
      deliveryAddress
      transactionId
      district
      transactionTotal
    }
  }
`;

export const GET_APPOINTMENT_SUMMARY_BY_DATE = gql`
  query GetAppointmentSummaryByDate($date: String!) {
    getAppointmentSummaryByDate(date: $date) {
      appointmentDateTime
      status
      payment
      extraMed
      deliveryWaybill
      timestamp
      lastnameEn
      firstnameEn
      chineseName
      gender
      dob
      hkid
      appUserPhone
      deliveryAddress
      transactionId
      district
      transactionTotal
    }
  }
`;

export const GET_APPOINTMENT_SUMMARY_BY_DATE_RANGE = gql`
  query GetAppointmentSummaryByDateRange(
    $startDate: String!
    $endDate: String!
    $reportType: String
    $tenant: String
    $payor: InsurancePayor
  ) {
    getAppointmentSummaryByDateRange(
      startDate: $startDate
      endDate: $endDate
      reportType: $reportType
      tenant: $tenant
      payor: $payor
    ) {
      # Default report fields
      appointmentDateTime
      status
      payment
      extraMed
      deliveryWaybill
      timestamp
      lastnameEn
      firstnameEn
      chineseName
      gender
      dob
      hkid
      appUserPhone
      deliveryAddress
      transactionId
      district
      transactionTotal
      tenant
      payor
      # SF Waybill export fields
      appointmentId
      date
      englishName
      waybillNo
      address
      mobile
      doctorName
    }
  }
`;

export const GET_CLAIMS_EXPORT_BY_DATE_RANGE = gql`
  query GetClaimsExportByDateRange($payor: InsurancePayor!, $startDate: String!, $endDate: String!) {
    getClaimsExportByDateRange(payor: $payor, startDate: $startDate, endDate: $endDate) {
      claimId
      policyNumber
      policyName
      planName
      appointmentId
      appointmentDate
      approvedAmount
      patientName
      doctorName
      clinicName
    }
  }
`;

// Agora Queries
export const GET_APPOINTMENT_AGORA_LINK = gql`
  query GetAppointmentAgoraLink($input: GetAppointmentAgoraLinkInput!) {
    getAppointmentAgoraLink(input: $input) {
      id
      appointmentId
      channelName
      uid
      role
      token
      link
      webLink
      appId
      expiresAt
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const GENERATE_AGORA_TOKEN = gql`
  mutation GenerateAgoraToken($input: GenerateTokenInput!) {
    generateAgoraToken(input: $input) {
      token
      appId
      channelName
      uid
      role
      expiresAt
    }
  }
`;

export const VALIDATE_AGORA_TOKEN = gql`
  query ValidateAgoraToken($input: ValidateTokenInput!) {
    validateAgoraToken(input: $input) {
      isValid
      message
    }
  }
`;

export const GET_AGORA_APP_ID = gql`
  query GetAgoraAppId {
    getAgoraAppId
  }
`;

export const GET_USER_APPOINTMENTS = gql`
  query GetUserAppointments($userId: String!) {
    userAppointments(userId: $userId) {
      id
      app_user_id
      provider_id
      date
      time
      duration
      status
      type
      reservation_type
      questionnaire
      appointment_notes
      priority
      consultation_type
      language_preference
      booking_source
      payment_status
      payment_method
      total_amount
      insurance_claim_id
      timezone
      reminder_sent
      confirmation_sent
      rescheduled_from
      rescheduled_to
      cancelled_reason
      cancelled_by
      medical_history_reviewed
      diagnosis
      treatment_plan
      prescription
      follow_up_required
      follow_up_date
      patient_phone
      patient_email
      emergency_contact
      special_instructions
      patient_rating
      patient_feedback
      doctor_notes
      quality_score
      tenant
      created_at
      updated_at
      scheduled_at
      confirmed_at
      started_at
      completed_at
      cancelled_at
      provider {
        id
        doctor_id
        clinic_id
        consultation_fee
        doctor {
          id
          first_name_en
          last_name_en
          first_name_zh_hk
          last_name_zh_hk
          first_name_zh_cn
          last_name_zh_cn
          specialization
          title
        }
        clinic {
          id
          name_en
          name_zh_hk
          name_zh_cn
          internal_name
        }
      }
    }
  }
`;

export const GET_PROVIDER_APPOINTMENTS = gql`
  query GetProviderAppointments($providerId: String!) {
    providerAppointments(providerId: $providerId) {
      id
      app_user_id
      provider_id
      date
      time
      duration
      status
      type
      reservation_type
      questionnaire
      appointment_notes
      priority
      consultation_type
      language_preference
      booking_source
      payment_status
      payment_method
      total_amount
      insurance_claim_id
      timezone
      reminder_sent
      confirmation_sent
      rescheduled_from
      rescheduled_to
      cancelled_reason
      cancelled_by
      medical_history_reviewed
      diagnosis
      treatment_plan
      prescription
      follow_up_required
      follow_up_date
      patient_phone
      patient_email
      emergency_contact
      special_instructions
      patient_rating
      patient_feedback
      doctor_notes
      quality_score
      tenant
      created_at
      updated_at
      scheduled_at
      confirmed_at
      started_at
      completed_at
      cancelled_at
      provider {
        id
        doctor_id
        clinic_id
        consultation_fee
        doctor {
          id
          first_name_en
          last_name_en
          first_name_zh_hk
          last_name_zh_hk
          first_name_zh_cn
          last_name_zh_cn
          specialization
          title
        }
        clinic {
          id
          name_en
          name_zh_hk
          name_zh_cn
          internal_name
        }
      }
    }
  }
`;

export const GET_DOCTOR_APPOINTMENTS = gql`
  query GetDoctorAppointments($doctorId: String!, $filter: AppointmentFilterDto) {
    doctorAppointments(doctorId: $doctorId, filter: $filter) {
      id
      app_user_id
      patient_id
      provider_id
      date
      time
      duration
      status
      queue_number
      type
      reservation_type
      questionnaire
      appointment_notes
      priority
      consultation_type
      language_preference
      booking_source
      payment_status
      payment_method
      total_amount
      insurance_claim_id
      timezone
      reminder_sent
      confirmation_sent
      rescheduled_from
      rescheduled_to
      cancelled_reason
      cancelled_by
      medical_history_reviewed
      diagnosis
      treatment_plan
      prescription
      follow_up_required
      follow_up_date
      patient_phone
      patient_email
      emergency_contact
      special_instructions
      patient_rating
      patient_feedback
      doctor_notes
      quality_score
      created_at
      updated_at
      scheduled_at
      confirmed_at
      started_at
      completed_at
      cancelled_at
      otherStatuses
      app_user {
        id
        firstname
        lastname
        email
        phone_number
        tenant
      }
      patient {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        gender
        birthdate
        email
        phone_number
        status
        relationship_to_owner
      }
      provider {
        id
        doctor_id
        clinic_id
        consultation_fee
        doctor {
          id
          first_name_en
          last_name_en
          first_name_zh_hk
          last_name_zh_hk
          specialization
          title
        }
        clinic {
          id
          name_en
          name_zh_hk
          internal_name
          address
          region
          district
        }
      }
    }
  }
`;

// Transaction Queries
export const GET_TRANSACTIONS = gql`
  query GetTransactions {
    transactions {
      id
      app_user_id
      appointment_id
      transaction_reference
      amount
      currency
      type
      status
      payment_method
      description
      category
      subcategory
      notes
      gateway_transaction_id
      gateway_response
      processing_fee
      tax_amount
      discount_amount
      final_amount
      metadata
      refund_reason
      refunded_by
      refunded_at
      original_transaction_id
      created_at
      updated_at
      processed_at
      completed_at
    }
  }
`;

export const GET_MY_TRANSACTIONS = gql`
  query GetMyTransactions {
    myTransactions {
      id
      app_user_id
      appointment_id
      transaction_reference
      amount
      currency
      type
      status
      payment_method
      description
      category
      subcategory
      notes
      gateway_transaction_id
      gateway_response
      processing_fee
      tax_amount
      discount_amount
      final_amount
      metadata
      refund_reason
      refunded_by
      refunded_at
      original_transaction_id
      created_at
      updated_at
      processed_at
      completed_at
    }
  }
`;

export const GET_APPOINTMENT_TRANSACTIONS = gql`
  query GetAppointmentTransactions($appointmentId: String!) {
    appointmentTransactions(appointmentId: $appointmentId) {
      id
      app_user_id
      appointment_id
      transaction_reference
      original_amount
      amount
      currency
      type
      status
      payment_method
      description
      category
      subcategory
      notes
      gateway_transaction_id
      gateway_response
      processing_fee
      tax_amount
      discount_amount
      final_amount
      metadata
      refund_reason
      refunded_by
      refunded_at
      original_transaction_id
      created_at
      updated_at
      processed_at
      completed_at
    }
  }
`;

export const GET_TRANSACTION = gql`
  query GetTransaction($id: String!) {
    transaction(id: $id) {
      id
      app_user_id
      appointment_id
      transaction_reference
      amount
      currency
      type
      status
      payment_method
      description
      category
      subcategory
      notes
      gateway_transaction_id
      gateway_response
      processing_fee
      tax_amount
      discount_amount
      final_amount
      metadata
      refund_reason
      refunded_by
      refunded_at
      original_transaction_id
      created_at
      updated_at
      processed_at
      completed_at
    }
  }
`;

export const GET_TRANSACTION_BY_REFERENCE = gql`
  query GetTransactionByReference($transactionReference: String!) {
    transactionByReference(transactionReference: $transactionReference) {
      id
      app_user_id
      appointment_id
      transaction_reference
      amount
      currency
      type
      status
      payment_method
      description
      category
      subcategory
      notes
      gateway_transaction_id
      gateway_response
      processing_fee
      tax_amount
      discount_amount
      final_amount
      metadata
      refund_reason
      refunded_by
      refunded_at
      original_transaction_id
      created_at
      updated_at
      processed_at
      completed_at
    }
  }
`;

export const GET_TRANSACTION_STATS = gql`
  query GetTransactionStats($appUserId: String) {
    transactionStats(appUserId: $appUserId) {
      totalTransactions
      totalAmount
      totalPayments
      totalRefunds
      averageAmount
    }
  }
`;

export const GENERATE_TRANSACTION_REFERENCE = gql`
  query GenerateTransactionReference {
    generateTransactionReference
  }
`;

// Close Buffer Minutes
export const GET_CLOSE_BUFFER_MINUTES_QUERY = gql`
  query GetCloseBufferMinutes {
    closeBufferMinutes
  }
`;

export const SET_CLOSE_BUFFER_MINUTES_MUTATION = gql`
  mutation SetCloseBufferMinutes($minutes: Float!) {
    setCloseBufferMinutes(minutes: $minutes)
  }
`;

// Queue Number Threshold
export const GET_QUEUE_NUMBER_THRESHOLD_QUERY = gql`
  query GetQueueNumberThreshold {
    queueNumberThreshold
  }
`;

export const SET_QUEUE_NUMBER_THRESHOLD_MUTATION = gql`
  mutation SetQueueNumberThreshold($number: Float!) {
    setQueueNumberThreshold(number: $number)
  }
`;

// Appointment Entry Buffer Minutes
export const GET_APPOINTMENT_ENTRY_BUFFER_MINUTES_QUERY = gql`
  query GetAppointmentEntryBufferMinutes {
    appointmentEntryBufferMinutes
  }
`;

export const SET_APPOINTMENT_ENTRY_BUFFER_MINUTES_MUTATION = gql`
  mutation SetAppointmentEntryBufferMinutes($minutes: Float!) {
    setAppointmentEntryBufferMinutes(minutes: $minutes)
  }
`;

// Default Delivery Contact Info
export const GET_DEFAULT_DELIVERY_CONTACT_INFO = gql`
  query GetDefaultDeliveryContactInfo {
    defaultDeliveryContactInfo {
      contact
      mobile
      address
    }
  }
`;

export const SET_DEFAULT_DELIVERY_CONTACT_INFO = gql`
  mutation SetDefaultDeliveryContactInfo($contact: String!, $mobile: String!, $address: String!) {
    setDefaultDeliveryContactInfo(contact: $contact, mobile: $mobile, address: $address)
  }
`;

export const GET_DELIVERY_CONTACT_LIST = gql`
  query GetDeliveryContactList {
    deliveryContactList {
      id
      contact
      mobile
      address
    }
  }
`;

export const SET_DELIVERY_CONTACT_LIST = gql`
  mutation SetDeliveryContactList($contacts: [DeliveryContactInput!]!) {
    setDeliveryContactList(contacts: $contacts)
  }
`;

// App User Queries
export const GET_APP_USERS = gql`
  query GetAppUsers {
    users {
      id
      email
      firstname
      lastname
      phone_number
      tenant
      language_preference
      ic_type
      ic_number
      birthdate
      age
      gender
      created_at
      updated_at
      deleted_at
      deleted_reason
      addresses {
        id
        type
        display_name
        region
        district
        street_address
        building_name
        floor
        block
        unit
        postal_code
        is_default
        phone_number
        contact_name
        created_at
        updated_at
      }
      patients {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        email
        phone_number
        birthdate
        gender
        status
        relationship_to_owner
        created_at
        updated_at
      }
    }
  }
`;

export const GET_APP_USERS_PAGINATED = gql`
  query GetAppUsersPaginated($paginationDto: PaginationDto!) {
    usersPaginated(paginationDto: $paginationDto) {
      users {
        id
        email
        firstname
        lastname
        phone_number
        tenant
        language_preference
        ic_type
        ic_number
        birthdate
        age
        gender
        created_at
        updated_at
        deleted_at
        deleted_reason
        addresses {
          id
          type
          display_name
          region
          district
          street_address
          building_name
          floor
          block
          unit
          postal_code
          is_default
          phone_number
          contact_name
          created_at
          updated_at
        }
        patients {
          id
          first_name_en
          last_name_en
          first_name_zh_hk
          last_name_zh_hk
          email
          phone_number
          birthdate
          gender
          status
          relationship_to_owner
          created_at
          updated_at
        }
      }
      pagination {
        page
        limit
        total
        totalPages
        hasNext
        hasPrev
      }
    }
  }
`;

export const GET_APP_USER = gql`
  query GetAppUser($id: ID!) {
    user(id: $id) {
      id
      email
      firstname
      lastname
      phone_number
      tenant
      language_preference
      ic_type
      ic_number
      birthdate
      age
      gender
      created_at
      updated_at
      deleted_at
      deleted_reason
      addresses {
        id
        type
        display_name
        region
        district
        street_address
        building_name
        floor
        block
        unit
        postal_code
        is_default
        phone_number
        contact_name
        created_at
        updated_at
      }
      patients {
        id
        first_name_en
        last_name_en
        first_name_zh_hk
        last_name_zh_hk
        email
        phone_number
        birthdate
        gender
        status
        relationship_to_owner
        created_at
        updated_at
      }
    }
  }
`;

export const GET_SF_EXPRESS_ORDERS_FOR_APPOINTMENT = gql`
  query GetSfExpressOrdersForAppointment($appointmentId: String!) {
    getSfExpressOrdersForAppointment(appointmentId: $appointmentId) {
      id
      tenant
      request_id
      service_code
      timestamp
      partner_id
      msg_digest
      request_data
      response_data
      status
      order_id
      waybill_no
      tracking_number
      error_code
      error_message
      api_result_code
      api_error_msg
      notes
      created_at
      updated_at
      processed_at
      retry_count
      last_retry_at
      metadata
      appointment_id
    }
  }
`;


// SF Express Route Search Queries
export const SEARCH_SF_EXPRESS_ROUTES = gql`
  query SearchSfExpressRoutes($input: SfExpressRouteSearchInput!) {
    searchSfExpressRoutes(input: $input) {
      success
      errorCode
      errorMsg
      data {
        waybillNo
        orderId
        trackingNumber
        routeInfo
        estimatedDelivery
        currentStatus
        currentLocation
        lastUpdateTime
        deliveryAddress
        senderAddress
      }
    }
  }
`;

export const SEARCH_SF_EXPRESS_ROUTES_BY_APPOINTMENT_ID = gql`
  query SearchSfExpressRoutesByAppointmentId($appointmentId: String!) {
    searchSfExpressRoutesByAppointmentId(appointmentId: $appointmentId) {
      success
      errorCode
      errorMsg
      data {
        waybillNo
        orderId
        trackingNumber
        routeInfo
        estimatedDelivery
        currentStatus
        currentLocation
        lastUpdateTime
        deliveryAddress
        senderAddress
      }
    }
  }
`;

// JD Express Queries
export const GET_JD_EXPRESS_ORDERS_FOR_APPOINTMENT = gql`
  query GetJdExpressOrdersForAppointment($appointmentId: String!) {
    getJdExpressOrdersForAppointment(appointmentId: $appointmentId) {
      id
      tenant
      request_id
      service_code
      timestamp
      partner_id
      msg_digest
      request_data
      response_data
      status
      order_id
      waybill_no
      tracking_number
      error_code
      error_message
      api_result_code
      api_error_msg
      notes
      created_at
      updated_at
      processed_at
      retry_count
      last_retry_at
      metadata
      appointment_id
    }
  }
`;

// Unified Express Orders Query
export const GET_EXPRESS_ORDERS_FOR_APPOINTMENT = gql`
  query GetExpressOrdersForAppointment($appointmentId: String!) {
    getExpressOrdersForAppointment(appointmentId: $appointmentId) {
      sfExpressOrders {
        id
        tenant
        request_id
        service_code
        timestamp
        partner_id
        msg_digest
        request_data
        response_data
        status
        order_id
        waybill_no
        tracking_number
        error_code
        error_message
        api_result_code
        api_error_msg
        notes
        created_at
        updated_at
        processed_at
        retry_count
        last_retry_at
        metadata
        appointment_id
      }
      jdExpressOrders {
        id
        tenant
        request_id
        service_code
        timestamp
        partner_id
        msg_digest
        request_data
        response_data
        status
        order_id
        waybill_no
        tracking_number
        error_code
        error_message
        api_result_code
        api_error_msg
        notes
        created_at
        updated_at
        processed_at
        retry_count
        last_retry_at
        metadata
        appointment_id
      }
    }
  }
`;

// Insurance Queries
export const GET_INSURANCE_PAYORS = gql`
  query GetInsurancePayors {
    insurancePayors
  }
`;

export const GET_INSURANCE_POLICIES_BY_PAYOR = gql`
  query GetInsurancePoliciesByPayor($payor: InsurancePayor!) {
    insurancePoliciesByPayor(payor: $payor) {
      id
      name
      payor
      planId
      policyNumber
      startDate
      endDate
      appUserId
      description
      policyDetails {
        free_consultations
        free_consultations_per_year
        free_consultations_per_month
        free_consultations_per_week
      }
      isActive
      createdAt
      updatedAt
      plan {
        id
      name
        payor
        description
        planDetails {
          free_consultations
          free_consultations_per_year
          free_consultations_per_month
          free_consultations_per_week
        }
      }
      appUser {
        id
        firstname
        lastname
        email
        phone_number
      }
    }
  }
`;

export const GET_INSURANCE_POLICIES_BY_PAYOR_PAGINATED = gql`
  query GetInsurancePoliciesByPayorPaginated($payor: InsurancePayor!, $paginationDto: PaginationDto!) {
    insurancePoliciesByPayorPaginated(payor: $payor, paginationDto: $paginationDto) {
      policies {
        id
        name
        payor
        planId
        policyNumber
        startDate
        endDate
        appUserId
        description
        policyDetails {
          free_consultations
          free_consultations_per_year
          free_consultations_per_month
          free_consultations_per_week
        }
        isActive
        createdAt
        updatedAt
        plan {
          id
          name
          payor
          description
          planDetails {
            free_consultations
            free_consultations_per_year
            free_consultations_per_month
            free_consultations_per_week
            copayment_amount
            coverage {
              name
              description
              type
              coverage_amount
              copayment_amount
              coverage_times_per_month
            }
          }
        }
        appUser {
          id
          firstname
          lastname
          email
          phone_number
        }
      }
      pagination {
        page
        limit
        total
        totalPages
        hasNext
        hasPrev
      }
    }
  }
`;

export const GET_INSURANCE_PLANS_BY_PAYOR = gql`
  query GetInsurancePlansByPayor($payor: InsurancePayor!) {
    insurancePlansByPayor(payor: $payor) {
      id
      name
      payor
      description
      planDetails {
        free_consultations
        free_consultations_per_year
        free_consultations_per_month
        free_consultations_per_week
        copayment_amount
        coverage {
          name
          description
          type
          coverage_amount
          copayment_amount
          coverage_times_per_month
        }
      }
      createdAt
      updatedAt
    }
  }
`;

export const GET_IS_COUPON_PAYOR = gql`
  query IsCouponPayor($payor: InsurancePayor!) {
    isCouponPayor(payor: $payor)
  }
`;

export const GET_INSURANCE_COUPONS_BY_PAYOR = gql`
  query GetInsuranceCouponsByPayor($payor: InsurancePayor!) {
    insuranceCouponsByPayor(payor: $payor) {
      id
      payor
      couponCode
      startDate
      endDate
      appUserId
      appointmentId
      policyDetails {
        free_consultations
        free_consultations_per_year
        free_consultations_per_month
        free_consultations_per_week
      }
      isActive
      createdAt
      updatedAt
      plan {
        id
        name
        payor
        description
        planDetails {
          free_consultations
          free_consultations_per_year
          free_consultations_per_month
          free_consultations_per_week
        }
      }
      appUser {
        id
        firstname
        lastname
        email
        phone_number
      }
      appointment {
        id
        date
        time
        status
        type
      }
    }
  }
`;

export const GET_INSURANCE_COUPONS_BY_PAYOR_PAGINATED = gql`
  query GetInsuranceCouponsByPayorPaginated($payor: InsurancePayor!, $paginationDto: PaginationDto!) {
    insuranceCouponsByPayorPaginated(payor: $payor, paginationDto: $paginationDto) {
      coupons {
        id
        payor
        couponCode
        startDate
        endDate
        appUserId
        appointmentId
        policyDetails {
          free_consultations
          free_consultations_per_year
          free_consultations_per_month
          free_consultations_per_week
        }
        isActive
        createdAt
        updatedAt
        plan {
          id
          name
          payor
          description
          planDetails {
            free_consultations
            free_consultations_per_year
            free_consultations_per_month
            free_consultations_per_week
          }
        }
        appUser {
          id
          firstname
          lastname
          email
          phone_number
        }
        appointment {
          id
          date
          time
          status
          type
        }
      }
      pagination {
        page
        limit
        total
        totalPages
        hasNext
        hasPrev
      }
    }
  }
`;

export const GET_UNUSED_INSURANCE_COUPONS_BY_PAYOR = gql`
  query GetUnusedInsuranceCouponsByPayor($payor: InsurancePayor!) {
    unusedInsuranceCouponsByPayor(payor: $payor) {
      id
      couponCode
      startDate
      endDate
      isActive
      createdAt
      plan {
        id
        name
      }
    }
  }
`;

export const GET_INSURANCE_COUPONS_BY_PLAN = gql`
  query GetInsuranceCouponsByPlan($planId: ID!) {
    insuranceCouponsByPlan(planId: $planId) {
      id
      payor
      couponCode
      startDate
      endDate
      appUserId
      appointmentId
      policyDetails {
        free_consultations
        free_consultations_per_year
        free_consultations_per_month
        free_consultations_per_week
      }
      isActive
      createdAt
      updatedAt
      plan {
        id
        name
        payor
        description
      }
      appUser {
        id
        firstname
        lastname
        email
        phone_number
      }
    }
  }
`;

export const GET_POLICY_CONSUMPTION_DETAILS = gql`
  query GetPolicyConsumptionDetails($policyId: ID!) {
    insuranceEligibilityCheckResult(policyId: $policyId) {
      insurancePolicyId
      copaymentAmount
      coverageDetails {
        freeConsultations
        freeConsultationsPerYear
        freeConsultationsPerMonth
        freeConsultationsPerWeek
        remainingCoverageAmount
      }
    }
  }
`;

export const GET_INSURANCE_ELIGIBILITY_BY_CATEGORY = gql`
  query InsuranceEligibilityCheckByCategory($policyId: ID!, $category: CoverageType!) {
    insuranceEligibilityCheckByCategory(policyId: $policyId, category: $category) {
      insurancePolicyId
      copaymentAmount
      coverageDetails {
        freeConsultations
        freeConsultationsPerYear
        freeConsultationsPerMonth
        freeConsultationsPerWeek
        remainingCoverageAmount
      }
    }
  }
`;

export const GET_INSURANCE_POLICIES_BY_USER = gql`
  query GetInsurancePoliciesByUser($appUserId: ID!) {
    insurancePoliciesByUser(appUserId: $appUserId) {
      id
      name
      payor
      planId
      policyNumber
      startDate
      endDate
      appUserId
      description
      policyDetails {
        free_consultations
        free_consultations_per_year
        free_consultations_per_month
        free_consultations_per_week
      }
      isActive
      createdAt
      updatedAt
    }
  }
`;

export const GET_INSURANCE_CLAIMS_BY_APPOINTMENT = gql`
  query GetInsuranceClaimsByAppointment($appointmentId: ID!) {
    insuranceClaimResultsByAppointment(appointmentId: $appointmentId) {
      id
      policyId
      appointmentId
      status
      claimResultDetails {
        approved_amount
        rejected_amount
        rejected_reason
      }
      isActive
      createdAt
      updatedAt
      items {
        id
        insuranceClaimId
        name
        category
        amount
        approvedAmount
        transactionId
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_INSURANCE_CLAIMS_BY_POLICY = gql`
  query GetInsuranceClaimsByPolicy($policyId: ID!) {
    insuranceClaimResultsByPolicy(policyId: $policyId) {
      id
      policyId
      appointmentId
      status
      claimResultDetails {
        approved_amount
        rejected_amount
        rejected_reason
      }
      isActive
      createdAt
      updatedAt
      items {
        id
        insuranceClaimId
        name
        category
        amount
        approvedAmount
        transactionId
        createdAt
        updatedAt
      }
    }
  }
`;

export const GET_INSURANCE_CLAIM_BY_ID = gql`
  query GetInsuranceClaimById($id: ID!) {
    insuranceClaimResult(id: $id) {
      id
      policyId
      appointmentId
      status
      claimResultDetails {
        approved_amount
        rejected_amount
        rejected_reason
      }
      isActive
      createdAt
      updatedAt
      items {
        id
        insuranceClaimId
        name
        category
        amount
        approvedAmount
        transactionId
        createdAt
        updatedAt
      }
    }
  }
`;
